/**
 * Column headings for the Scope and Findings Summary tables.
 *
 * Deliberately a mirror of SCOPE_TABLE_LABELS / FINDINGS_SUMMARY_LABELS and
 * their resolvers in backend/utils/report-helpers.js, as riskRatingDefaults.js
 * and nextStepsDefaults.js already mirror theirs. The settings panel shows
 * these literals as the value of every control, so if one side changes and the
 * other does not, the editor starts lying about what the report will say.
 *
 * ROWS ARE NOT HERE, deliberately. Both tables are filled from the engagement
 * at generation time, and that is data rather than template content. What a
 * template may say is what the columns are CALLED.
 *
 * The keys of FINDINGS_SUMMARY_LABELS are the keys config.columns holds, so a
 * column's visibility and its heading are addressed by one name.
 */

export const SCOPE_TABLE_LABELS = { type: 'Type', target: 'Target', description: 'Description' };
export const SCOPE_TABLE_EMPTY = 'No scope items defined.';

export const FINDINGS_SUMMARY_LABELS = {
  '#': '#', title: 'Finding', severity: 'Severity', cvss: 'CVSS',
  directlyExploitable: 'Exploitable', status: 'Status',
};

/**
 * A deliberately blank heading is a real choice, so '' is kept. Only a missing
 * or non-string value falls back, which is what lets a template saved before a
 * heading was editable keep printing the heading it always printed.
 */
export function resolveColumnLabels(defaults, given) {
  const labels = { ...defaults };
  const src = (given && typeof given === 'object') ? given : {};
  Object.keys(labels).forEach(key => {
    if (typeof src[key] === 'string') labels[key] = src[key];
  });
  return labels;
}

/**
 * The columns a findings-summary prints when its config names none.
 *
 * Both renderers have always carried this fallback, so a section with no
 * `columns` key prints four columns -- while the editor's tick boxes, which
 * read `config.columns || []`, showed none of them ticked. Ticking one then
 * wrote a single-column list, and the table silently dropped from four columns
 * to one. Sharing the fallback is what makes the panel describe what prints.
 */
export const FINDINGS_SUMMARY_DEFAULT_COLUMNS = ['#', 'title', 'severity', 'cvss'];

export function findingsSummaryColumns(config) {
  const given = (config || {}).columns;
  return Array.isArray(given) ? given : [...FINDINGS_SUMMARY_DEFAULT_COLUMNS];
}

export function scopeTableConfig(config) {
  const cfg = config || {};
  return {
    labels: resolveColumnLabels(SCOPE_TABLE_LABELS, cfg.columnLabels),
    emptyText: typeof cfg.emptyText === 'string' ? cfg.emptyText : SCOPE_TABLE_EMPTY,
  };
}

export function findingsSummaryLabels(config) {
  return resolveColumnLabels(FINDINGS_SUMMARY_LABELS, (config || {}).columnLabels);
}
